import React from "react";
import { useForm, UseFormReturn } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  ContactInformationFormData,
  BusinessProfileFormData,
  PersonalProfileFormData,
  contactInformationSchema,
  businessProfileSchema,
  personalProfileSchema,
} from "./_validation";

type FormStep = "contact" | "business" | "personal" | "success";

interface FormContextType {
  step: FormStep;
  setStep: (step: FormStep) => void;
  contactMethods: UseFormReturn<ContactInformationFormData>;
  businessMethods: UseFormReturn<BusinessProfileFormData>;
  personalMethods: UseFormReturn<PersonalProfileFormData>;
  contactData: ContactInformationFormData | null;
  setContactData: (data: ContactInformationFormData) => void;
  handleBack: () => void;
  isSubmitting: boolean;
  setIsSubmitting: (value: boolean) => void;
  handleFormSubmit: (
    data: ContactInformationFormData | BusinessProfileFormData | PersonalProfileFormData,
    formType: "contact" | "business" | "personal"
  ) => Promise<void>;
  submitError: string | null;
  setSubmitError: (error: string | null) => void;
}

const FormContext = React.createContext<FormContextType | undefined>(undefined);

interface CombinedFormData {
  contact: ContactInformationFormData;
  profile: BusinessProfileFormData | PersonalProfileFormData;
}

export const FormProvider = ({ children }: { children: React.ReactNode }) => {
  const [step, setStep] = React.useState<FormStep>("contact");
  const [contactData, setContactData] = React.useState<ContactInformationFormData | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [submitError, setSubmitError] = React.useState<string | null>("iub iunibnt riunr ");
  const [formData, setFormData] = React.useState<Partial<CombinedFormData>>({});

  const handleFormSubmit = async (
    data: ContactInformationFormData | BusinessProfileFormData | PersonalProfileFormData,
    formType: "contact" | "business" | "personal"
  ) => {
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      switch (formType) {
        case "contact":
          const contactFormData = data as ContactInformationFormData;
          setContactData(contactFormData);
          setFormData(prev => ({ ...prev, contact: contactFormData }));
          if (contactFormData.profileType !== "none") {
            setStep(contactFormData.profileType);
          }
          break;

        case "business":
        case "personal":
          if (!contactData) {
            throw new Error("Contact information is required");
          }

          let payload: Record<string, string> = {
            firstName: contactData.firstName,
            lastName: contactData.lastName,
            phone: contactData.phone,
          };

          if (formType === "business") {
            const businessData = data as BusinessProfileFormData;
            const businessPayload = {
              formType: "business",
              companyName: businessData.companyName,
              companyEmail: businessData.companyEmail,
              industry: businessData.industry,
              purchaseVolume: businessData.purchaseVolume,
              region: businessData.region,
              notificationPreferences: Object.entries(businessData.notificationPreferences)
                .filter(([_, enabled]) => enabled)
                .map(([type]) => type)
                .join(", "),
              purchaseTimeline: businessData.purchaseTimeline,
              privacyPolicy: businessData.privacyPolicy.toString(),
            };

            payload = {
              ...payload,
              ...businessPayload,
            };
          }

          if (formType === "personal") {
            const personalData = data as PersonalProfileFormData;
            const personalPayload = {
              formType: "personal",
              personalEmail: personalData.personalEmail,
              productCategories: personalData.productCategories.join(", "),
              notificationPreferences: Object.entries(personalData.notificationPreferences)
                .filter(([_, enabled]) => enabled)
                .map(([type]) => type)
                .join(", "),
              purchaseTimeline: personalData.purchaseTimeline,
              privacyPolicy: personalData.privacyPolicy.toString(),
            };

            payload = {
              ...payload,
              ...personalPayload,
            };
          }

          const response = await fetch("/api/submit-form", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
          });

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || "Failed to submit form");
          }

          setStep("success");
          break;
      }
    } catch (error) {
      console.error("Form submission error:", error);
      setSubmitError(
        error instanceof Error ? error.message : "Something went wrong. Please try again later."
      );
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactMethods = useForm<ContactInformationFormData>({
    resolver: zodResolver(contactInformationSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      phone: "",
      profileType: "none",
    },
  });

  const businessMethods = useForm<BusinessProfileFormData>({
    resolver: zodResolver(businessProfileSchema),
    mode: "onBlur",
    defaultValues: {
      notificationPreferences: {
        email: false,
        sms: false,
      },
      privacyPolicy: false,
    },
  });

  const personalMethods = useForm<PersonalProfileFormData>({
    resolver: zodResolver(personalProfileSchema),
    mode: "onBlur",
    defaultValues: {
      notificationPreferences: {
        email: false,
        sms: false,
      },
      privacyPolicy: false,
    },
  });

  const handleBack = () => setStep("contact");

  return (
    <FormContext.Provider
      value={{
        step,
        setStep,
        contactMethods,
        businessMethods,
        personalMethods,
        contactData,
        setContactData,
        handleBack,
        isSubmitting,
        setIsSubmitting,
        handleFormSubmit,
        submitError,
        setSubmitError,
      }}
    >
      {children}
    </FormContext.Provider>
  );
};

export const useFormContext = () => {
  const context = React.useContext(FormContext);
  if (context === undefined) {
    throw new Error("useFormContext must be used within a FormProvider");
  }
  return context;
};
